import { z } from 'zod';
import {} from './base-pricing.js';
import {} from './margin.js';
import { chargeableArea } from './waste.js';
import { CustomerServiceCatalogue, CustomerServiceSelection, SkirtingHeightBand, PROPOSAL_WORK_NOTE_MAX_LENGTH } from './proposal-services.js';
import { Offer } from './customer-offer.js';
import { RoomChoiceOffer } from './room-choice-offer.js';
const money = z.number().finite().nonnegative().max(1e9);
const range = z.object({ min: money, max: money });
/** One sell line, and nothing else. There is no field here for material, labour,
 *  contribution, supplier, price basis or COD — so none can leak by omission.
 *
 *  `unitSell` is the LINE TOTAL DIVIDED BY THE QUANTITY SHOWN ABOVE IT — the rate per
 *  unit a customer reads as "$38.50/m²". Jacky, 17 September, reading a released
 *  proposal: *"I need to see it on proposal the rate per unit"*. It is a derivation of
 *  `sell`, not a new figure from the engine, so it cannot disclose anything `sell`
 *  does not; and it is computed on the CUSTOMER'S unit. */
/**
 * ONE COST ITEM BEHIND AN OPEN-BOOK LINE — quantity × unit cost = subtotal (Jacky, 15 Oct 2026: "display
 * cost using new line for each item, proper formatted with Quantity, UOM, Per price, sub total"). Only an
 * open-book offer carries these; a standard Proposal's lines keep the empty default, and the boundary test
 * holds costs out of everything else.
 */
export const CostItem = z.object({ label: z.string().min(1).max(200), quantity: z.number().finite().nonnegative().max(1e8), unit: z.string().min(1).max(20), unit_cost: z.number().finite().min(-1e9).max(1e9), cost: z.number().finite().min(-1e9).max(1e9) });
export const CustomerPricedLine = z.object({ work_item_key: z.string().max(240).optional(),
    selection_id: z.string().max(80).optional(),
    label: z.string().min(1).max(300),
    /** The sub-category this line belongs to. A recommended line carries it so a customer's CHOICE can
     *  replace the right line: choose the Hybrid alternative and the Hybrid line goes, nothing else. */
    group: z.string().trim().max(60).default(''),
    colour: z.string().max(160).nullable().default(null),
    unit: z.string().min(1).max(20),
    quantity: z.number().finite().nonnegative().max(1e8),
    /** Saved physical floor basis, separate from the purchased/order quantity. Never inferred from price. */
    measured_quantity: z.number().finite().nonnegative().max(1e8).optional(),
    quantity_multiplier: z.number().finite().min(1).max(2).optional(),
    /** A manually quoted order quantity is not evidence of a cutting/waste allowance. */
    quantity_source: z.enum(['manual', 'assessment']).optional(),
    unitSell: z.number().finite().min(-1e9).max(1e9).nullable().default(null),
    unitSellRange: range.nullable().default(null),
    sell: z.number().finite().min(-1e9).max(1e9).nullable(),
    sellRange: range.nullable(),
    notices: z.array(z.string().max(500)).max(10).default([]),
    /**
     * WHAT KIND OF LINE THIS IS — the customer reads them differently.
     *
     * Jacky, 18 Sep: *"remeber the accessories that is binded to main product, customer will see indent line
     * showing its included in the price"*, and *"any additional work I add in, I can tick entire item is
     * optional"*. A service is work; an accessory bound to a product is part of that product's price and
     * carries NO figure; a variation is priced but only charged if it is used.
     */
    kind: z.enum(['product', 'service', 'accessory', 'variation']).default('product'),
    /** True for the bound accessory: shown indented, "included in the price", with no amount of its own. */
    included_in_price: z.boolean().default(false),
    /** Selling language printed under the line. Never a cost. */
    note: z.string().max(PROPOSAL_WORK_NOTE_MAX_LENGTH).default(''),
    /** A proposal-safe catalogue shown only after this line is selected. */
    selection_catalogue: CustomerServiceCatalogue.nullable().default(null),
    /** Height band already carried by the quoted installed rate. */
    included_height_band: SkirtingHeightBand.nullable().default(null),
    /** Open-book only: the items this line's amount is the sum of. Empty on every standard Proposal. */
    cost_items: z.array(CostItem).max(30).default([]),
});
/**
 * The choice a customer is offered instead of the recommended product.
 *
 * Jacky's model: *"I recommend a products and I select or Emma pick a bunch of other product in same
 * category … I setup a GM rate or Markup %, all other product selected by customer list by that
 * rate."* So an option is a real, priced alternative listed at HIS rate — and deliberately NOT part of
 * the total, because a choice is not a purchase. The subtitle carries the rate sentence so the
 * customer reads why every option in a group is priced the way it is.
 */
export const CustomerOptionGroup = z.object({
    target_selection_id: z.string().max(80).optional(),
    group: z.string().min(1).max(60),
    /** What the customer sees as the group's name, in their language (15 Oct 2026). The `group` id stays the key a choice is posted under. */
    title: z.string().max(120).nullable().default(null),
    /** Selling language only, exactly like `notices`. */
    note: z.string().max(PROPOSAL_WORK_NOTE_MAX_LENGTH).nullable().default(null),
    /** A pool of PRODUCT alternatives, or a piece of optional WORK they may take or leave. */
    kind: z.enum(['product', 'service']).default('product'),
    /**
     * True when the customer must choose one for the work to be complete — and false when the work is
     * offered and they may decline it. Either way the group is never in the totals until it is chosen.
     */
    required: z.boolean().default(false),
    // The pick list carries `kind` and `note` too: a chosen SERVICE has to arrive in the accepted scope
    // knowing it is work rather than a product, and with the sentence that explains it. selection_id lets a product story find its
    // option by id rather than position (24 Oct 2026).
    options: z.array(CustomerPricedLine.pick({ work_item_key: true, selection_id: true, label: true, colour: true, unit: true, quantity: true, unitSell: true, unitSellRange: true, sell: true, sellRange: true, notices: true, kind: true, included_in_price: true, note: true, selection_catalogue: true, included_height_band: true, cost_items: true })).max(200),
});
export const CustomerPricing = z.object({ charge_rounding: z.literal('line_down').optional(),
    /** The configured GST rate the customer figures use (Jacky, 26 Oct 2026: GST is the configured rate on the
     * rounded ex-GST subtotal). Absent on offers projected before it existed; then the engine's ratio is used. */
    gst_rate: z.number().min(0).max(1).optional(),
    lines: z.array(CustomerPricedLine).max(200),
    /** The pools the customer may choose from, one entry per sub-category. Never in the totals above. */
    optionGroups: z.array(CustomerOptionGroup).max(30).default([]),
    subtotal: money.nullable(),
    subtotalRange: range.nullable(),
    gst: money.nullable(),
    total: money.nullable(),
    totalRange: range.nullable(),
    /** Shown above the lines. Selling language only. */
    notices: z.array(z.string().max(500)).max(10).default([]),
    /**
     * WORK THAT IS PRICED AND NOT CHARGED UNLESS IT IS USED.
     *
     * Jacky: *"I also add item that is subject to variation, price is set, not added to pricing unless used,
     * and customer will approve during installation when I need to use it."* Deliberately its own list: a
     * variation is not a choice the customer makes now, and it is not part of the total — it is shown so
     * there is no surprise later, with the price already agreed.
     */
    variations: z.array(CustomerPricedLine).max(40).default([]),
    /**
     * OPEN-BOOK: the lines are COSTS and the margin is added once, here (Jacky, 15 Oct 2026, for a builder:
     * "I will lay out all quote item cost to Artemis FLR. and seek GM of 20%" … "only add 20% GM at bottom
     * total"). `subtotal` is still the price the customer accepts; `cost_subtotal` + `margin` = `subtotal`.
     * Null on every standard Proposal, which is how the boundary test tells the two apart.
     */
    open_book: z.object({ margin_rate: z.number().min(0).max(0.9), cost_subtotal: money, margin: money }).nullable().default(null),
    /** Open-book with per-room choice (15 Oct 2026): what Artemis offers, frozen with the rules of the day. The lines above are the DEFAULT selection; `priceSelection` re-prices any other. */
    offer: Offer.nullable().default(null),
    /** Room choice at SELL prices (18 Oct 2026, Minto): one all-in rate per product, groups, pins, fixed lines — no cost field exists in it. The lines above are the recommended selection; `priceRoomChoice` re-prices any other. */
    room_choice: RoomChoiceOffer.nullable().default(null),
});
/** Re-settle an open-book offer's totals from its charged lines: cost → price at the margin, GST on the price. */
export function settleOpenBook(pricing) {
    if (!pricing.open_book)
        return pricing;
    const charged = pricing.lines.filter(l => !l.included_in_price);
    if (!charged.every(l => l.sell !== null))
        return CustomerPricing.parse({ ...pricing, subtotal: null, gst: null, total: null, subtotalRange: null, totalRange: null });
    const cost = cash(charged.reduce((n, l) => n + (l.sell ?? 0), 0));
    const subtotal = cash(cost / (1 - pricing.open_book.margin_rate)), gst = cash(subtotal * 0.1);
    return roundDownToDollar(CustomerPricing.parse({ ...pricing, subtotal, subtotalRange: null, gst, total: cash(subtotal + gst), totalRange: null, open_book: { ...pricing.open_book, cost_subtotal: cost, margin: cash(subtotal - cost) } }));
}
/**
 * The one crossing from engine figures to customer copy.
 *
 * Rules, in order:
 *   1. Only sell prices cross. `material`, `labour`, `internal.*`, `costBasis`,
 *      supplier names and staff `warnings` are not read at all.
 *   2. A line with no price of any kind blocks the copy. It is named for staff
 *      and nothing is published — an unpriced proposal never reaches a customer.
 *   3. A $0 line is dropped, and reported to staff as dropped.
 *   4. A line priced as a range is kept as a range with its notice. An undecided
 *      colour therefore reads *from $418.47*, not as a missing number.
 *   5. Carpet is quoted in purchased m²: whole LM × full roll width. The
 *      engine still costs it in LM. No roll width means no invented m².
 *   6. Wastage, when the caller supplies it, moves the CHARGEABLE area of a line
 *      whose quantity is a floor area — the quantity, never the price (`waste.ts`).
 */
export function projectCustomerPricing(result, pricing, optionLines = [], margin = null, work = {}) {
    // Only the RECOMMENDED selections line up with this result — the API prices the option pool in its
    // own engine call, so the option lines arrive separately and are never part of this quote's total.
    if (!result.lines.length && !pricing.products.length && !pricing.services.length && !pricing.access && !pricing.minimum && result.gstRate !== undefined)
        result = { ...result, subtotal: 0, gst: 0, total: 0, subtotalRange: null, settledRange: null };
    const recommended = pricing.products.filter(p => p.role !== 'option');
    const optionSelections = pricing.products.filter(p => p.role === 'option');
    const productLines = result.lines.slice(0, recommended.length);
    const otherLines = result.lines.slice(recommended.length);
    const blockers = [];
    const zeroLines = [];
    const lines = [];
    const isCarpet = (selection) => /carpet/i.test(selection.group) ||
        (work.accessories ?? []).some(a => a.bound_to === selection.productId && a.id.startsWith('carpet-default-'));
    const aligned = result.lines.length >= recommended.length;
    const rows = recommended.map((selection, index) => ({ selection, line: aligned ? productLines[index] : undefined, other: false }))
        .concat(otherLines.map(line => ({ line, other: true })));
    for (const row of rows) {
        const line = row.line;
        if (!line)
            continue;
        // A range whose ends are equal is one price (every colour costs the same): it is shown, totalled and rounded as one.
        const rawRange = line.revenueRange ?? null, lineRange = singleFigure(rawRange);
        const revenue = line.revenue ?? (rawRange && !lineRange ? rawRange.min : null);
        if (!row.other && line.unit === 'LM' && row.selection && isCarpet(row.selection) && !row.selection.carpetRollWidthM) {
            blockers.push(`${line.label} — full roll width needed to quote purchased m²`);
            continue;
        }
        if (row.other && (revenue === 0 || lineRange?.min === 0 && lineRange.max === 0)) {
            zeroLines.push(line.label);
            continue;
        }
        if (revenue === null && !lineRange) {
            blockers.push(`${line.label} — ${line.warnings[0] ?? 'price to be confirmed'}`);
            continue;
        }
        // Carpet: bill the purchased roll area, including each cut's allowance and
        // whole-LM rounding. The assessed floor area is not a purchase quantity.
        const selection = row.selection;
        const carpetShown = !row.other && line.unit === 'LM' && !!selection?.carpetRollWidthM;
        const purchasedArea = carpetShown ? Math.round(line.quantity * selection.carpetRollWidthM * 100) / 100 : 0;
        /**
         * WASTAGE COMES OFF THE AREA, NOT THE MONEY.
         *
         * Jacky: *"area SQM need also wastage … customer will see we will charge in the end by actual"*. A line
         * whose quantity IS a floor area — carpet shown in m² of floor, or a hard floor the engine already costs
         * in m², which is where a herringbone or chevron floor lives — is shown the CHARGEABLE area with the
         * sentence that explains it. The engine's price is untouched: this only moves the quantity its rate is
         * read over, and the customer is told the charge is by actual measurement.
         *
         * `areaM2` is the floor the caller measured for this offer; a line's own area is the fallback for a
         * caller that states none. With no `waste` argument every figure below is the engine's, as before.
         */
        const areaShown = line.unit === 'm²' ? line.quantity : 0;
        const charge = selection?.quantity_source !== 'manual' && work.waste && areaShown > 0 ? chargeableArea(work.waste.areaM2 > 0 ? work.waste.areaM2 : areaShown, work.waste.pattern, work.waste.rules) : null;
        /**
         * THE WASTE IS A SENTENCE HERE, NOT A QUANTITY — and that is deliberate.
         *
         * Jacky: *"area SQM need also wastage … customer will see we will charge in the end by actual"*. The
         * allowance has to reach the PRICE, which means the engine has to be asked to price the chargeable
         * area — so the quantity is settled when the line is priced, not when the copy is written. Moving only
         * the quantity here leaves the engine's money where it was and divides it over a bigger number, which
         * prints a LOWER rate per m² than the customer is being charged: a discount nobody offered.
         *
         * What this function can honestly add is the explanation, beside the rate it belongs to.
         */
        const shownQty = carpetShown ? purchasedArea : line.quantity;
        const per = (amount) => amount === null || !(shownQty > 0) ? null : Math.round((amount / shownQty) * 100) / 100;
        lines.push(CustomerPricedLine.parse({
            ...(pricing.minimum && line.material === 0 && line.labour === 0 && line.label.endsWith(' — minimum adjustment') ? { work_item_key: 'price-adjustment:' + pricing.minimum } : {}),
            ...(selection ? { work_item_key: 'floor:' + selection.id } : {}), selection_id: selection?.id,
            ...(selection?.quantity_source ? { quantity_source: selection.quantity_source } : {}),
            ...(line.unit === 'm²' && selection?.measured_quantity !== undefined ? { measured_quantity: selection.measured_quantity, quantity_multiplier: selection.quantity_multiplier } : {}),
            kind: row.other ? 'service' : 'product',
            label: selection?.description || line.label,
            group: selection?.group ?? '',
            colour: selection ? selection.colour : null,
            unit: carpetShown ? 'm²' : line.unit,
            quantity: shownQty,
            unitSell: per(revenue),
            unitSellRange: lineRange && shownQty > 0 ? { min: Math.round((lineRange.min / shownQty) * 100) / 100, max: Math.round((lineRange.max / shownQty) * 100) / 100 } : null,
            sell: revenue,
            sellRange: lineRange,
            notices: charge && charge.note ? [...(line.notices ?? []), charge.note] : (line.notices ?? []),
        }));
    }
    const notices = [];
    if (lines.some(l => l.sellRange))
        notices.push('One or more lines depend on a colour that is not chosen yet. The final price is confirmed when you choose.');
    // The pools, priced at his rate by the API. A pool with nothing priced in it is dropped rather than
    // published as a list of dashes.
    const optionGroups = [];
    for (const [index, selection] of optionSelections.entries()) {
        const line = optionLines[index];
        if (!line)
            continue;
        if (line.unit === 'LM' && isCarpet(selection) && !selection.carpetRollWidthM) {
            blockers.push(`${line.label} — full roll width needed to quote purchased m²`);
            continue;
        }
        // The API has already priced this pool at the proposal's rate (`margin.ts`), so the projection
        // takes the figure as it arrives and does no arithmetic of its own — one place computes a price.
        const sell = line.revenue;
        if (sell === null || sell === 0)
            continue;
        const candidates = recommended.filter(p => selection.replaces_id ? p.id === selection.replaces_id : p.group === selection.group);
        if (candidates.length !== 1) {
            blockers.push(`${selection.description || line.label} — choose one scope line for this alternative`);
            continue;
        }
        const target = candidates[0];
        const baseGroup = selection.group || line.label.split('—')[0].trim() || 'Options';
        const label = target.group || baseGroup;
        const group = recommended.filter(p => p.group === target.group).length > 1 ? `${label.slice(0, 24)} · ${(target.rooms.join(', ') || target.description || target.id).slice(0, 30)}`.slice(0, 60) : label;
        if (optionGroups.some(g => g.group === group && g.target_selection_id !== target.id)) {
            blockers.push(`${group} — alternative scope labels must be distinct`);
            continue;
        }
        const carpetOption = line.unit === 'LM' && !!selection.carpetRollWidthM;
        const shownQty = carpetOption ? Math.round(line.quantity * selection.carpetRollWidthM * 100) / 100 : line.quantity;
        const unit = carpetOption ? 'm²' : line.unit;
        const priced = CustomerPricedLine.parse({
            // The option's own selection, so a product story finds it by id rather than by its position in the group (24 Oct 2026).
            work_item_key: 'floor:' + selection.id, selection_id: selection.id,
            label: selection.description || line.label,
            group,
            colour: selection.colour,
            unit,
            quantity: shownQty,
            unitSell: shownQty > 0 ? Math.round((sell / shownQty) * 100) / 100 : null,
            unitSellRange: null,
            sell,
            sellRange: null,
            notices: line.notices ?? [],
        });
        const found = optionGroups.find(g => g.group === group);
        if (found)
            found.options.push(priced);
        else
            optionGroups.push(CustomerOptionGroup.parse({ group, target_selection_id: target.id, note: null, options: [priced] }));
    }
    // THE WORK JOINS THE OFFER. Removal, disposal, skirting, stairs, accessories — priced by the
    // `artemis_services` config and by his own custom lines, never by the engine (which prices products).
    // When there is no work, every figure below is exactly the engine's, untouched.
    const worked = projectWork(work.services ?? [], work.accessories ?? []);
    optionGroups.push(...worked.groups);
    if (worked.lines.length) {
        lines.push(...worked.lines);
        // The GST RATE comes from the engine's own figures rather than a number written here: one place
        // decides what tax is. With nothing priced to divide by, nothing is added.
        const rate = result.gstRate ?? (result.subtotal && result.subtotal > 0 && result.gst !== null ? result.gst / result.subtotal : null);
        const servicesSubtotal = worked.subtotal;
        const addGst = rate === null ? null : Math.round(servicesSubtotal * rate * 100) / 100;
        const subtotal = result.subtotal === null ? null : Math.round((result.subtotal + servicesSubtotal) * 100) / 100;
        const gst = result.gst === null || addGst === null ? result.gst : Math.round((result.gst + addGst) * 100) / 100;
        // The ranges move with the work too. Before 17 Oct 2026 only the subtotal did, so a colour range covered the floor line
        // alone ($2,642.40 under a $3,169.96 offer on Fiona Zhu's P-1026-10297) and the round-down skipped the "range".
        const shift = (r, by) => r ? { min: Math.round((r.min + by) * 100) / 100, max: Math.round((r.max + by) * 100) / 100 } : null;
        result = { ...result, subtotal, gst, total: subtotal === null || gst === null ? result.total : Math.round((subtotal + gst) * 100) / 100,
            subtotalRange: shift(result.subtotalRange, servicesSubtotal), settledRange: shift(result.settledRange, servicesSubtotal + (addGst ?? 0)) };
    }
    notices.push(...worked.notices);
    const priced = blockers.length === 0 && (lines.length > 0 || optionGroups.length > 0 || worked.variations.length > 0);
    const pricingOut = priced ? CustomerPricing.parse({
        charge_rounding: pricing.charge_rounding,
        lines,
        optionGroups,
        variations: worked.variations,
        subtotal: result.subtotal ?? (result.subtotalRange && !singleFigure(result.subtotalRange) ? result.subtotalRange.min : null),
        subtotalRange: singleFigure(result.subtotalRange),
        gst: result.gst,
        gst_rate: result.gstRate,
        total: result.total ?? (result.settledRange && !singleFigure(result.settledRange) ? result.settledRange.min : null),
        totalRange: singleFigure(result.settledRange),
        notices,
    }) : null;
    if (worked.unpriced.length)
        blockers.push(...worked.unpriced.map(n => `${n} — no rate approved yet`));
    return { pricing: pricingOut?.charge_rounding === 'line_down' ? roundChargeLinesDown(pricingOut) : pricingOut, blockers, zeroLines };
}
/** Carpet lines are the ones the engine costs in LM. Exposed so the staff rail can
 *  say which unit it is showing without re-deriving the rule. */
export function isCarpetLine(unit) { return unit === 'LM'; }
export const StaffProposalDecision = z.object({
    group: z.string().trim().min(1).max(60),
    status: z.enum(['accepted', 'declined', 'unresolved']),
    optionIndex: z.number().int().min(0).max(199).nullable().default(null),
    agreedUnitSell: money.nullable().default(null),
    selection: CustomerServiceSelection.nullable().default(null),
}).superRefine((v, ctx) => {
    if (v.status === 'accepted' && v.optionIndex === null)
        ctx.addIssue({ code: 'custom', message: 'An accepted group needs the agreed option' });
    if (v.status !== 'accepted' && (v.optionIndex !== null || v.agreedUnitSell !== null || v.selection !== null))
        ctx.addIssue({ code: 'custom', message: 'Only an accepted group can carry an option or agreed rate' });
});
/** Apply a staff-recorded offline decision to the frozen customer copy. Every
 * offered group remains in the evidence as accepted, declined or unresolved;
 * only accepted groups enter the charged scope. An agreed rate changes the
 * selected line and the total, never the issued snapshot. */
export function applyStaffProposalDecisions(pricing, decisions) {
    const accepted = decisions.filter(d => d.status === 'accepted').map(d => ({ group: d.group, optionIndex: d.optionIndex, selection: d.selection ?? undefined }));
    const applied = applyChoices(pricing, accepted);
    const rateByGroup = new Map(decisions.filter(d => d.status === 'accepted' && d.agreedUnitSell !== null).map(d => [d.group, d.agreedUnitSell]));
    const lines = applied.pricing.lines.map(line => {
        const rate = rateByGroup.get(line.group);
        return rate === undefined ? line : { ...line, unitSell: rate, unitSellRange: null, sell: cash(rate * line.quantity), sellRange: null };
    });
    const charged = lines.filter(l => !l.included_in_price), subtotal = charged.every(l => l.sell !== null) ? cash(charged.reduce((n, l) => n + (l.sell ?? 0), 0)) : null;
    const sourceSubtotal = applied.pricing.subtotal, sourceGst = applied.pricing.gst;
    const gstRate = applied.pricing.gst_rate ?? (sourceSubtotal && sourceGst !== null ? sourceGst / sourceSubtotal : .1);
    const gst = subtotal === null ? null : cash(subtotal * gstRate), total = subtotal === null || gst === null ? null : cash(subtotal + gst);
    return { ...applied, pricing: CustomerPricing.parse({ ...applied.pricing, lines, subtotal, gst, total, subtotalRange: null, totalRange: null }) };
}
/** Price a frozen service option from its quoted installed rate plus the height movement only.
 * Wall material already decided whether the quote is $19 or $20/LM; catalogue selection must never
 * replace that rate with a standalone board price. Profile does not alter price. */
export function priceServiceSelection(option, selection) {
    const catalogue = option.selection_catalogue;
    const item = catalogue && selection?.catalogue_id === catalogue.id ? catalogue.items.find(i => i.id === selection.item_id) : undefined;
    const variant = item?.variants.find(v => v.id === selection?.variant_id);
    const included = option.included_height_band;
    const includedAdjustment = included && catalogue ? catalogue.items.flatMap(i => i.variants).find(v => v.height_band === included && v.price_adjustment_per_lm !== null)?.price_adjustment_per_lm : null;
    const selectedAdjustment = variant?.price_adjustment_per_lm ?? null;
    const rate = option.unitSell !== null && typeof includedAdjustment === 'number' && typeof selectedAdjustment === 'number' ? cash(option.unitSell + selectedAdjustment - includedAdjustment) : option.unitSell;
    if (rate === option.unitSell)
        return option;
    return { ...option, unitSell: rate, sell: rate === null ? null : cash(rate * option.quantity) };
}
/** When a Proposal carries a catalogue selection, it must be one that was frozen into the offer.
 * No selection is also valid: traditional household customers commonly confirm this detail with
 * Artemis after acceptance, while the work is being prepared as a Job. */
export function choiceSelectionErrors(pricing, choices) {
    const errors = [];
    const seen = new Set();
    for (const choice of choices) {
        if (seen.has(choice.group)) {
            errors.push(`${choice.group}: choose only one option`);
            continue;
        }
        seen.add(choice.group);
        const group = pricing.optionGroups.find(g => g.group === choice.group);
        const option = group?.options[choice.optionIndex];
        if (!group || !option) {
            errors.push(`${choice.group}: this option is not in the issued offer`);
            continue;
        }
        const catalogue = option.selection_catalogue;
        if (!catalogue)
            continue;
        const selected = choice.selection;
        if (!selected)
            continue;
        const item = selected && selected.catalogue_id === catalogue.id ? catalogue.items.find(i => i.id === selected.item_id) : undefined;
        const variant = item?.variants.find(v => v.id === selected?.variant_id);
        if (!item || !variant)
            errors.push(`${group.group}: the selected profile and size are not in this offer`);
    }
    return errors;
}
export function applyChoices(pricing, choices) {
    const byGroup = new Map(choices.map((c) => [c.group, c.optionIndex]));
    const replaced = [];
    const chosen = [];
    const lines = [];
    const takenGroups = new Set();
    for (const line of pricing.lines) {
        const group = pricing.optionGroups.find(g => g.target_selection_id ? g.target_selection_id === line.selection_id : !!line.group && g.group === line.group);
        const pick = group ? byGroup.get(group.group) : undefined;
        // Legacy issued copies may have ambiguous category names. Never replace multiple scopes with one option.
        if (group && !group.target_selection_id && pick !== undefined && pricing.lines.filter(l => l.group === group.group).length > 1)
            throw Error('This alternative covers more than one scope. Request a revised Proposal.');
        const option = pick === undefined || !group ? undefined : group.options[pick];
        if (!option) {
            lines.push(line);
            continue;
        }
        replaced.push(line.label);
        chosen.push(option.label);
        // The chosen alternative takes the recommended line's place, at the price it was LISTED at — its own
        // quantity and colour come with it, because that is the product the customer picked.
        lines.push({ ...option, selection_id: line.selection_id, group: line.group });
        takenGroups.add(group.group);
    }
    /**
     * OPTIONAL WORK THEY CHOSE — the chosen scope must BE in the accepted proposal.
     *
     * Jacky's objective, verbatim: *"proposal sent to customer should include all the scope of work
     * depending on choosen option of customer on skirting work"*. An optional service is offered OUTSIDE the
     * total (a choice is not a purchase), so choosing one has nothing to replace — it has to be ADDED, at
     * the price it was listed at. Same rule as a product choice: the figure was priced when the offer was
     * released and is never re-calculated here.
     */
    for (const group of pricing.optionGroups) {
        if (group.kind !== 'service' || takenGroups.has(group.group))
            continue;
        const pick = byGroup.get(group.group);
        if (pick === undefined)
            continue;
        const rawOption = group.options[pick];
        const choice = choices.find(c => c.group === group.group && c.optionIndex === pick);
        const option = rawOption && priceServiceSelection(rawOption, choice?.selection);
        if (!option)
            continue;
        const catalogue = option.selection_catalogue;
        const item = catalogue && choice?.selection?.catalogue_id === catalogue.id ? catalogue.items.find(i => i.id === choice.selection.item_id) : undefined;
        const variant = item?.variants.find(v => v.id === choice?.selection?.variant_id);
        const selectedLabel = item && variant ? `${item.label} · ${variant.label}` : '';
        chosen.push(selectedLabel ? `${option.label} — ${selectedLabel}` : option.label);
        lines.push({ ...option, group: group.group,
            label: selectedLabel ? `${option.label} — ${selectedLabel}` : option.label,
            note: selectedLabel ? [option.note, `Customer selection: ${selectedLabel}.`].filter(Boolean).join(' ') : option.note,
        });
    }
    /**
     * AN "INCLUDED IN THE PRICE" LINE HAS NO FIGURE, AND THAT MUST NOT WIPE THE TOTAL.
     *
     * Found by the live end-to-end: the customer's copy carries the accessory bound to the product as an
     * indented line with `sell: null` and `included_in_price: true` — it is already inside the product's
     * price. The first version of this sum required EVERY line to carry a figure, so choosing an option on
     * an offer holding one bound accessory produced a null subtotal and a null total: the acceptance would
     * have recorded no price at all.
     *
     * Lines that are included in another line's price are left out of the arithmetic, not counted as zero
     * and not treated as blockers.
     */
    const charged = lines.filter((l) => !l.included_in_price);
    const subtotal = charged.every((l) => l.sell !== null) ? cash(charged.reduce((n, l) => n + (l.sell ?? 0), 0)) : null;
    const gst = subtotal === null ? null : cash(subtotal * gstFrom(pricing));
    const total = subtotal === null || gst === null ? null : cash(subtotal + gst);
    const settled = CustomerPricing.parse({ ...pricing, lines, subtotal, subtotalRange: null, gst, total, totalRange: null });
    // An open-book offer's lines are costs: the chosen options joined at cost, so the price is re-settled once, at the total.
    // A price RELEASED under the round-down (a whole-dollar total) stays under it through the customer's choices. A copy
    // released before 16 Oct 2026 keeps its cents: its page lists no rounding line, and flooring it here would show a
    // total its lines do not add up to, and change a released price at acceptance. Re-publishing brings it under the rule.
    const released = pricing.subtotal !== null && Math.round(pricing.subtotal * 100) % 100 === 0;
    const open = settleOpenBook(settled);
    return { pricing: released ? roundDownToDollar(open) : open, replaced, chosen };
}
const cash = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
/** The GST rate the released offer used, read back from it — a published 10% stays 10%. */
function gstFrom(pricing) {
    // The configured rate, when the offer carries it. Before 26 Oct 2026 the engine's own ratio was reused after the
    // whole-dollar round-down, so a $678.60 engine subtotal (GST $67.40, total rounded to $746.00) became $678.00 with
    // GST $67.34: not 10% of the subtotal the customer saw (Seabrook test Proposal, harness cycles).
    if (pricing.gst_rate !== undefined)
        return pricing.gst_rate;
    if (pricing.subtotal && pricing.gst !== null && pricing.subtotal > 0)
        return pricing.gst / pricing.subtotal;
    return 0.1;
}
const r2 = (n) => Math.round(n * 100) / 100;
export function projectWork(services = [], accessories = []) {
    const lines = [];
    const groups = [];
    const variations = [];
    const unpriced = [];
    const notices = [];
    let subtotal = 0;
    for (const s of services) {
        if (s.offered === false)
            continue;
        const priced = (rate, label = s.name) => CustomerPricedLine.parse({
            work_item_key: 'service:' + s.id + (s.chosen_option ? ':' + s.chosen_option : ''), label, group: '', colour: null, unit: s.unit, quantity: s.quantity,
            unitSell: rate, unitSellRange: null,
            sell: rate === null ? null : r2(rate * s.quantity), sellRange: null,
            notices: [], kind: s.variation ? 'variation' : 'service', included_in_price: false, note: s.note ?? '',
        });
        /**
          * A VARIATION IS QUOTED AS A RATE, NOT AS A QUANTITY — because nobody knows the quantity yet.
          *
          * Jacky, 19 Sep: *"how am I suppose to quote by variation? Door trim and step? I dont know the
          * quantity, it suppose to say if needed, this how much each. I can optional say step here and step
          * there (typing it out)"*. So the line reads "if needed — $50 each", the customer approves it on the
          * day, and a quantity is OPTIONAL: he may type the steps here and there, and if he does the copy says
          * what it comes to, but a variation with no quantity is complete and quotable, never blank.
          *
          * It is never in the total, with or without a quantity.
          */
        if (s.variation) {
            const per = s.rate;
            const shownQty = s.quantity > 0 ? s.quantity : 0;
            /**
             * WHAT IT COMES TO, WHEN THE DRAWING ALREADY KNOWS.
             *
             * The canvas measures the steps, so "17 × $130 = $2,210" is a real figure and the customer should see
             * it — agreed now, charged only if it is used. Door trimming has no count, and there the rate alone is
             * the quote ("if needed — $50 each"). Either way it never enters the total.
             */
            const comesTo = per !== null && shownQty > 0 ? Math.round(shownQty * per * 100) / 100 : null;
            const line = CustomerPricedLine.parse({
                work_item_key: 'service:' + s.id, label: s.name, group: '', colour: null, unit: s.unit, quantity: shownQty,
                unitSell: per, unitSellRange: null,
                sell: comesTo, sellRange: null,
                notices: [], kind: 'variation', included_in_price: false,
                note: s.note || (per === null ? 'Rate to be confirmed.' : `Only if we use it — ${per} per ${s.unit}, approved by you on the day.`),
            });
            if (s.rate === null)
                unpriced.push(s.name);
            variations.push(line);
            continue;
        }
        // OPTIONAL WORK IS A CHOICE, NOT A PURCHASE. Its sub-options are the pool; his typical shape is two.
        if (s.optional) {
            const options = (s.options?.length ? s.options : s.rate !== null ? [{ id: 'include', label: s.name, rate: s.rate, note: s.note ?? '', catalogue: null, included_height_band: null }] : []).filter((o) => o.rate !== null).map((o) => CustomerPricedLine.parse({
                work_item_key: 'service:' + s.id + ':' + o.id, label: o.label, group: s.name, colour: null, unit: s.unit, quantity: s.quantity,
                unitSell: o.rate, unitSellRange: null, sell: r2((o.rate ?? 0) * s.quantity), sellRange: null,
                notices: [], kind: 'service', included_in_price: false, note: o.note ?? '', selection_catalogue: o.catalogue ?? null, included_height_band: o.included_height_band ?? null,
            }));
            if (!options.length) {
                unpriced.push(s.name);
                continue;
            }
            groups.push(CustomerOptionGroup.parse({
                group: s.name, note: s.note || null, kind: 'service', required: false, options,
            }));
            continue;
        }
        // INCLUDED WORK IS IN THE PRICE — and only when there is a rate to price it with.
        if (s.rate === null) {
            unpriced.push(s.name);
            continue;
        }
        const line = priced(s.rate);
        lines.push(line);
        subtotal += line.sell ?? 0;
    }
    for (const a of accessories) {
        if (!a.on)
            continue;
        // BOUND TO THE PRODUCT: part of that price, so it carries no figure of its own and reads as the
        // indented line he described. Anything else is a priced line like any other.
        if (a.bound_to) {
            lines.push(CustomerPricedLine.parse({
                work_item_key: 'accessory:' + a.id, label: a.name, group: '', colour: a.colour ?? null, unit: a.unit ?? 'm²', quantity: a.quantity ?? 0,
                unitSell: null, unitSellRange: null, sell: null, sellRange: null, notices: [],
                kind: 'accessory', included_in_price: true, note: a.note || 'Included in the price of the product above.',
            }));
            continue;
        }
        if (a.rate === null || a.rate === undefined || !(a.quantity ?? 0)) {
            unpriced.push(a.name);
            continue;
        }
        const line = CustomerPricedLine.parse({
            work_item_key: 'accessory:' + a.id, label: a.name, group: '', colour: a.colour ?? null, unit: a.unit ?? 'm²', quantity: a.quantity ?? 0,
            unitSell: a.rate, unitSellRange: null, sell: r2(a.rate * (a.quantity ?? 0)), sellRange: null, notices: [],
            kind: 'accessory', included_in_price: false, note: a.note ?? '',
        });
        lines.push(line);
        subtotal += line.sell ?? 0;
    }
    if (variations.length)
        notices.push('Items under "only if used" are priced and agreed now, and are charged only if we use them — you approve each one on the day.');
    if (groups.length)
        notices.push('Optional work is priced for you to choose; nothing is charged unless you choose it.');
    return { lines, groups, variations, subtotal: r2(subtotal), unpriced, notices };
}
/** A named commercial discount preserves every original quantity/rate and survives choice recalculation.
 *  The result is always rounded DOWN to the whole dollar (`roundDownToDollar`), with or without a discount. */
export function applyRoundingDiscount(pricing, discount, label = 'Rounding discount') {
    if (!Number.isFinite(discount) || discount < 0)
        throw Error('Invalid rounding discount');
    if (!discount)
        return roundDownToDollar(pricing, label);
    if (pricing.subtotal === null || discount > pricing.subtotal)
        throw Error('The rounding discount exceeds the current scope total');
    const subtotal = Math.round((pricing.subtotal - discount) * 100) / 100;
    const gst = pricing.gst === null ? null : Math.round(subtotal * gstFrom(pricing) * 100) / 100;
    return roundDownToDollar(CustomerPricing.parse({ ...pricing, lines: [...pricing.lines, CustomerPricedLine.parse({ selection_id: 'commercial-rounding', label, kind: 'service', unit: 'each', quantity: 1, unitSell: -discount, sell: -discount, sellRange: null })], subtotal, gst, total: gst === null ? null : Math.round((subtotal + gst) * 100) / 100, subtotalRange: null, totalRange: null }), label);
}
/**
 * THE TOTAL EX GST, ROUNDED DOWN TO THE WHOLE DOLLAR — always (Jacky, 16 October 2026: *"do total ex GST with rounding
 * to nearest whole dollar, always round down"*; asked whether that is the price or only its display: *"It is the
 * price"*). So the page, the PDF, the acceptance, the deposit and the invoice all carry the same whole-dollar figure.
 *
 * The cents come off on the one `commercial-rounding` line: an existing rounding discount grows by them, otherwise
 * the line is added. Quantities and rates are never touched. An open-book offer's lines are COSTS, so a line there
 * would be marked up by the margin; its margin absorbs the cents instead. A price still shown as a range, or with no
 * figure, is left alone. Idempotent: a whole-dollar subtotal is returned unchanged.
 */
/** A range whose two ends are the same figure is not a range. */
export function singleFigure(range) { return range && range.min !== range.max ? range : null; }
export function roundDownToDollar(pricing, label = 'Rounding discount') {
    if (pricing.charge_rounding === 'line_down')
        return roundChargeLinesDown(pricing);
    if (pricing.subtotalRange && !singleFigure(pricing.subtotalRange))
        pricing = { ...pricing, subtotalRange: null, totalRange: singleFigure(pricing.totalRange) };
    if (pricing.subtotal === null || pricing.subtotalRange !== null)
        return pricing;
    const cents = Math.round(pricing.subtotal * 100), whole = Math.floor(cents / 100) * 100;
    if (cents === whole || cents < 0)
        return pricing;
    const subtotal = whole / 100, off = (cents - whole) / 100, rate = gstFrom(pricing);
    const gst = pricing.gst === null ? null : cash(subtotal * rate), total = gst === null ? null : cash(subtotal + gst);
    if (pricing.open_book)
        return CustomerPricing.parse({ ...pricing, subtotal, gst, total, open_book: { ...pricing.open_book, margin: cash(subtotal - pricing.open_book.cost_subtotal) } });
    const at = pricing.lines.findIndex(l => l.selection_id === 'commercial-rounding');
    const lines = at < 0
        ? [...pricing.lines, CustomerPricedLine.parse({ selection_id: 'commercial-rounding', label, kind: 'service', unit: 'each', quantity: 1, unitSell: -off, sell: -off, sellRange: null })]
        : pricing.lines.map((l, i) => { if (i !== at)
            return l; const sell = cash((l.sell ?? 0) - off); return { ...l, sell, unitSell: sell }; });
    return CustomerPricing.parse({ ...pricing, lines, subtotal, gst, total, totalRange: null });
}
/** Convert an EX GST target into a separate discount without changing line quantities or rates. */
export function roundingDiscountForTotal(subtotal, target) {
    if (!Number.isFinite(subtotal) || !Number.isFinite(target) || subtotal < 0 || target < 0)
        throw Error('Invalid final total');
    const baseCents = Math.round(subtotal * 100), targetCents = Math.round(target * 100);
    if (!Number.isSafeInteger(baseCents) || !Number.isSafeInteger(targetCents) || targetCents > baseCents)
        throw Error('Final total exceeds scope');
    return (baseCents - targetCents) / 100;
}
/** Down to the whole dollar, tolerating float noise ($678.999999999 is $679). The one floor the charge lines and any
 *  figure shown beside a rounded total use, so a column never reads $678.60 next to a $678 total. */
export const wholeDollarDown = (n) => Math.floor(n + 1e-9);
/** An explicit selling discount policy: floor each charge before summing. Frozen with the offer and retained by choices. */
export function roundChargeLinesDown(pricing) {
    if (pricing.open_book)
        throw Error('Whole charge-line rounding applies to selling prices, not open-book cost lines.');
    const down = wholeDollarDown;
    const line = (l) => {
        if (l.included_in_price)
            return l;
        const sell = l.sell === null ? null : down(l.sell), sellRange = l.sellRange ? { min: down(l.sellRange.min), max: down(l.sellRange.max) } : null;
        return { ...l, sell, sellRange, unitSell: sell !== null && l.quantity > 0 ? sell / l.quantity : l.unitSell, unitSellRange: sellRange && l.quantity > 0 ? { min: sellRange.min / l.quantity, max: sellRange.max / l.quantity } : null };
    };
    const lines = pricing.lines.map(line), charged = lines.filter(l => !l.included_in_price), rate = gstFrom(pricing);
    const ranged = charged.some(l => l.sellRange && l.sellRange.min !== l.sellRange.max);
    const subtotal = !ranged && charged.every(l => l.sell !== null) ? cash(charged.reduce((n, l) => n + (l.sell ?? 0), 0)) : null;
    const subtotalRange = ranged && charged.every(l => l.sell !== null || l.sellRange) ? { min: cash(charged.reduce((n, l) => n + (l.sellRange?.min ?? l.sell ?? 0), 0)), max: cash(charged.reduce((n, l) => n + (l.sellRange?.max ?? l.sell ?? 0), 0)) } : null;
    const gst = subtotal === null || pricing.gst === null ? null : cash(subtotal * rate);
    return CustomerPricing.parse({ ...pricing, charge_rounding: 'line_down', lines, optionGroups: pricing.optionGroups.map(g => ({ ...g, options: g.options.map(line) })), variations: pricing.variations.map(line), subtotal, subtotalRange, gst, total: subtotal === null || gst === null ? null : cash(subtotal + gst), totalRange: subtotalRange ? { min: cash(subtotalRange.min + cash(subtotalRange.min * rate)), max: cash(subtotalRange.max + cash(subtotalRange.max * rate)) } : null });
}
//# sourceMappingURL=customer-pricing.js.map